import { Box, Flex, Text, Icon } from "@chakra-ui/react"
import { Lock, Check, Star } from "lucide-react"
import type { GameChapterItem, GameLevelStatus } from "@zrt-ai-office/types/course"

interface GameMapNodeProps {
  chapter: GameChapterItem
  index: number
  isLeft: boolean
  onClick: (chapterId: number) => void
}

/** Color schemes per level status */
const STATUS_STYLES: Record<
  GameLevelStatus,
  {
    bg: string
    border: string
    color: string
    hoverShadow: string
  }
> = {
  locked: {
    bg: "gray.200",
    border: "gray.300",
    color: "gray.400",
    hoverShadow: "none",
  },
  available: {
    bg: "emerald.500",
    border: "emerald.400",
    color: "white",
    hoverShadow: "0 0 20px rgba(16,185,129,0.45)",
  },
  completed: {
    bg: "emerald.500",
    border: "emerald.600",
    color: "white",
    hoverShadow: "0 0 12px rgba(16,185,129,0.3)",
  },
}

export function GameMapNode({ chapter, index, isLeft, onClick }: GameMapNodeProps) {
  const style = STATUS_STYLES[chapter.status]
  const isLocked = chapter.status === "locked"
  const isCompleted = chapter.status === "completed"
  const isAvailable = chapter.status === "available"

  return (
    <Flex
      direction="column"
      align="center"
      transform={isLeft ? "translateX(-60px)" : "translateX(60px)"}
      transition="transform 0.3s ease"
      cursor={isLocked ? "not-allowed" : "pointer"}
      onClick={() => onClick(chapter.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick(chapter.id)
        }
      }}
      aria-label={`${chapter.title} - ${chapter.status === "locked" ? "locked" : chapter.status === "completed" ? "completed" : "available"}`}
    >
      {/* Circle node */}
      <Box position="relative">
        <Flex
          align="center"
          justify="center"
          w="64px"
          h="64px"
          borderRadius="full"
          bg={style.bg}
          border="3px solid"
          borderColor={style.border}
          color={style.color}
          fontSize="xl"
          fontWeight="bold"
          position="relative"
          transition="all 0.2s ease"
          _hover={
            !isLocked
              ? {
                  transform: "scale(1.1)",
                  boxShadow: style.hoverShadow,
                }
              : undefined
          }
          /* Breathing animation for available status */
          animation={isAvailable ? "breathe 2s ease-in-out infinite" : undefined}
          boxShadow={isAvailable ? "0 0 16px rgba(16,185,129,0.4)" : "none"}
        >
          {isLocked && <Icon asChild boxSize="24px"><Lock /></Icon>}
          {isCompleted && <Icon asChild boxSize="24px"><Check strokeWidth={3} /></Icon>}
          {isAvailable && <Text fontSize="xl" fontWeight="bold">{index + 1}</Text>}
        </Flex>

        {/* Outer ring pulse for available */}
        {isAvailable && (
          <Box
            position="absolute"
            top="-4px"
            left="-4px"
            right="-4px"
            bottom="-4px"
            borderRadius="full"
            border="2px solid"
            borderColor="emerald.400"
            animation="pulse-ring 2s ease-in-out infinite"
            pointerEvents="none"
          />
        )}
      </Box>

      {/* Stars for completed */}
      {isCompleted && chapter.stars > 0 && (
        <Flex mt="1.5" gap="0.5">
          {[1, 2, 3].map((starIndex) => (
            <Icon
              key={starIndex}
              asChild
              boxSize="16px"
              color={starIndex <= chapter.stars ? "yellow.400" : "gray.300"}
            >
              <Star
                fill={starIndex <= chapter.stars ? "currentColor" : "none"}
              />
            </Icon>
          ))}
        </Flex>
      )}

      {/* Title */}
      <Text
        mt="2"
        fontSize="sm"
        fontWeight="medium"
        color={isLocked ? "gray.400" : "slate.700"}
        textAlign="center"
        maxW="120px"
        lineClamp={2}
      >
        {chapter.title}
      </Text>

      {/* Keyframes via global style injection */}
      <style>{`
        @keyframes breathe {
          0%, 100% { box-shadow: 0 0 8px rgba(16,185,129,0.3); }
          50% { box-shadow: 0 0 24px rgba(16,185,129,0.6); }
        }
        @keyframes pulse-ring {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0; transform: scale(1.15); }
        }
      `}</style>
    </Flex>
  )
}
